import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import MuestrasMovimientoCamara from "./MuestrasMovimientoCamara";
import { exportMuestrasToPdf } from "./muestrasPdfExport";
import { canSharePdfFile, isIosDevice } from "./muestrasPdfDelivery";
import MuestrasSavePdfToCompassDialog from "./MuestrasSavePdfToCompassDialog";

const makeFilename = () =>
  `Muestras_SDS_${new Date().toISOString().slice(0, 10)}.pdf`;

function guessHints(selectedMuestras) {
  const seasons = (selectedMuestras || [])
    .map((m) => String(m.TEMPORADA || m.Temporada || "").trim())
    .filter(Boolean);
  const seasonCounts = {};
  for (const s of seasons) seasonCounts[s] = (seasonCounts[s] || 0) + 1;
  const suggestedTemporada =
    Object.entries(seasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
  const customerIds = (selectedMuestras || [])
    .map((m) => Number(m.ID_CLIENTE || m.CustomerId || m.customerId))
    .filter((n) => Number.isFinite(n) && n > 0);
  const cidCounts = {};
  for (const id of customerIds) cidCounts[id] = (cidCounts[id] || 0) + 1;
  const suggestedCustomerId =
    Number(Object.entries(cidCounts).sort((a, b) => b[1] - a[1])[0]?.[0]) ||
    null;
  return { suggestedTemporada, suggestedCustomerId };
}

const MuestrasPresentacionCamara = ({
  muestras = [],
  embedded = false,
  onCreateMoodboard = null,
}) => {
  const [preparedPdf, setPreparedPdf] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [hints, setHints] = useState({
    suggestedTemporada: "",
    suggestedCustomerId: null,
  });

  useEffect(
    () => () => {
      if (preparedPdf?.url) URL.revokeObjectURL(preparedPdf.url);
    },
    [preparedPdf],
  );

  const generatePresentation = useCallback(
    async ({ items, setProgress }) => {
      const selectedIds = items.map((item) => Number(item.ID_MUESTRA));
      const selectedSet = new Set(selectedIds);
      const selectedMuestras = muestras.filter((muestra) =>
        selectedSet.has(Number(muestra.id ?? muestra.ID_MUESTRA)),
      );

      if (selectedMuestras.length !== selectedSet.size) {
        throw new Error(
          "No se han podido recuperar todas las muestras seleccionadas.",
        );
      }

      const filename = makeFilename();
      const blob = await exportMuestrasToPdf({
        muestras: selectedMuestras,
        selectedIds,
        filename,
        onProgress: ({ current, total }) => setProgress(current, total),
        download: false,
      });

      const file = new File([blob], filename, { type: "application/pdf" });
      setPreparedPdf((previous) => {
        if (previous?.url) URL.revokeObjectURL(previous.url);
        return {
          blob,
          file,
          filename,
          url: URL.createObjectURL(blob),
        };
      });
      setHints(guessHints(selectedMuestras));
      setSaveOpen(true);

      return {
        successMessage:
          "✅ Presentación lista. Elige dónde guardarla en Compás.",
      };
    },
    [muestras],
  );

  const sharePreparedPdf = async () => {
    if (!preparedPdf || sharing) return;
    setSharing(true);
    setShareError("");
    try {
      if (canSharePdfFile(navigator, preparedPdf.file)) {
        await navigator.share({
          files: [preparedPdf.file],
          title: "Presentación de muestras",
        });
        return;
      }
      window.open(preparedPdf.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      if (error?.name !== "AbortError") {
        setShareError(
          "No se pudo abrir el menú para guardar o compartir el PDF.",
        );
      }
    } finally {
      setSharing(false);
    }
  };

  const selectionFooter = preparedPdf ? (
    <Box sx={{ mt: 2, textAlign: "left" }}>
      <Alert severity="success" sx={{ mb: 1.5 }}>
        PDF preparado. Guárdalo en Compás o compártelo desde el dispositivo.
      </Alert>
      <Button
        variant="contained"
        fullWidth
        onClick={() => setSaveOpen(true)}
        sx={{ mb: 1, bgcolor: "#ad1457", "&:hover": { bgcolor: "#880e4f" } }}
        startIcon={<SaveOutlinedIcon />}
      >
        Guardar en Compás
      </Button>
      {isIosDevice() ? (
        <Button
          variant="outlined"
          fullWidth
          onClick={sharePreparedPdf}
          disabled={sharing}
          startIcon={
            sharing ? (
              <CircularProgress size={17} color="inherit" />
            ) : (
              <IosShareOutlinedIcon />
            )
          }
        >
          Guardar o compartir PDF
        </Button>
      ) : null}
      {shareError ? (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {shareError}
        </Typography>
      ) : null}
    </Box>
  ) : null;

  return (
    <>
      <MuestrasMovimientoCamara
        embedded={embedded}
        title={embedded ? null : "PRESENTACIÓN DE MUESTRAS"}
        confirmLabel="Generar presentación PDF"
        selectionOnly
        selectionListTitle="Presentación"
        customRegister={generatePresentation}
        selectionFooter={selectionFooter}
        hideCourier
        moodboardLabel="Crear moodboard"
        onMoodboard={
          typeof onCreateMoodboard === "function"
            ? (ids) => onCreateMoodboard(ids)
            : null
        }
      />
      <MuestrasSavePdfToCompassDialog
        open={saveOpen}
        blob={preparedPdf?.blob || null}
        filename={preparedPdf?.filename || makeFilename()}
        suggestedTemporada={hints.suggestedTemporada}
        suggestedCustomerId={hints.suggestedCustomerId}
        onClose={() => setSaveOpen(false)}
      />
    </>
  );
};

export default MuestrasPresentacionCamara;
